import React from 'react';
import tokens from '../design/tokens';
import { TAGLINE, noOrphan } from '../brand';

/**
 * Privacy and Terms — short, plain English, no legalese padding.
 * Same ivory ground and type system as the homepage; light only.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };

const CONTACT_EMAIL = 'info@byrdsense.com';
const CONTACT_PHONE = '(423) 790-4745';

const PRIVACY = {
  title: 'Privacy',
  eyebrow: 'HOW WE HANDLE YOUR INFORMATION',
  updated: 'October 2026',
  sections: [
    {
      h: 'What the request form collects',
      p: [
        `When you fill in the form on our homepage we collect your name, your company, and your phone number. That is all the form sends us.`,
        `We use it for one thing: to call you back about an equipment screening. We do not sell it or rent it, and we do not use it for advertising. It is stored on Amazon Web Services and sent to our email inbox (Microsoft 365), and goes nowhere else.`,
        `We keep request form details until you ask us to remove them.`,
      ],
    },
    {
      h: 'Cookies and tracking',
      p: [
        `This site sets no cookies and uses no tracking or analytics. Your browser saves one display setting (light or dark). If you sign in to the customer portal, your sign-in is also kept in your browser until you sign out.`,
      ],
    },
    {
      h: 'Monitoring data',
      p: [
        `If you become a monitoring customer, the sensors on your equipment send vibration and temperature readings, plus the sensor's own battery and status. That data is stored in Amazon Web Services in the US East (N. Virginia) region, us-east-1, with a protected backup copy in US West (Oregon), us-west-2.`,
        `Readings are tied to your company, your site, and the specific asset they came from. We do not record audio, video, or anything about people — only the machines.`,
      ],
    },
    {
      h: 'The data is yours',
      p: [
        `You own your monitoring data. Ask us for a copy at any time and we will send it to you in a normal file format.`,
        `When you ask us to delete your information, we remove it from our active records within one business day. Protected backup copies are removed within 90 days.`,
      ],
    },
    {
      h: 'Who can see it',
      p: [
        `Your portal login shows your equipment only. Byrdsense sees your data because we are the ones who call you about it.`,
        `We do not sell your data or use it for advertising. It is processed only by the companies that run the service for us: Amazon Web Services (hosting, storage, sign-in and sending email), Microsoft 365 (our email inbox), and Anthropic (an AI service we use to read equipment nameplate photos and thermal images, and to help write parts of screening reports).`,
      ],
    },
    {
      h: 'Questions',
      p: [
        `Email ${CONTACT_EMAIL} or call ${CONTACT_PHONE}. A person will answer.`,
      ],
    },
  ],
};

const TERMS = {
  title: 'Terms',
  eyebrow: 'WHAT WE AGREE TO',
  updated: 'September 2026',
  sections: [
    {
      h: 'What we do',
      p: [
        `We put vibration sensors on the equipment you choose, grade each reading against ISO 20816 (formerly ISO 10816) for that asset's size class, and tell you what we see. We review readings every business day, and if equipment crosses a limit, we call you — typically the same business day.`,
      ],
    },
    {
      h: 'What a grade is, and is not',
      p: [
        `A grade describes what an asset's readings show. It is a measurement and an informed opinion from a motor shop, not a guarantee about the future.`,
        `Machines can fail between readings, and some faults do not show up in vibration at all. Deciding when to take equipment out of service is your call. We give you the readings and our recommendation so you can make it.`,
      ],
    },
    {
      h: 'Service and uptime',
      p: [
        `Sensors run on batteries and report over a cellular gateway. Sometimes a sensor goes quiet — a dead battery, a gateway outage, a sensor knocked off a housing. We review sensor status every business day and follow up when one goes quiet, but we cannot promise unbroken coverage.`,
      ],
    },
    {
      h: 'Billing',
      p: [
        `Monitoring is billed monthly per site. Screenings are billed per visit. Prices and terms are in the quote we send you before any work starts; nothing is charged that is not on that quote.`,
        `You can cancel monitoring with 30 days' notice. We will arrange collection of the sensors.`,
      ],
    },
    {
      h: 'Your data',
      p: [
        `You own your monitoring data. See our Privacy page for where it is stored and how to get a copy or have it deleted.`,
      ],
    },
    {
      h: 'Questions',
      p: [
        `Email ${CONTACT_EMAIL} or call ${CONTACT_PHONE}.`,
      ],
    },
  ],
};

function LegalPage({ doc }) {
  return (
    <div style={{ position: 'relative', background: C.ground, color: C.ink, fontFamily: F.body, minHeight: '100vh' }}>
      <style>{`
        .bs-legal { padding-left: 72px; padding-right: 72px; }
        @media (max-width: 1023px) { .bs-legal { padding-left: 40px; padding-right: 40px; } }
        @media (max-width: 767px)  { .bs-legal { padding-left: 20px; padding-right: 20px; } }
        a:focus-visible { outline: 2px solid ${C.primary}; outline-offset: 2px; }
      `}</style>

      <div className="bs-legal" style={{ height: 84, boxSizing: 'border-box', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.hairline}` }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }} aria-label="Byrdsense home">
          <img src="/assets/brand/byrdsense-wordmark-color.svg" alt="Byrdsense" style={{ width: 180, height: 'auto', display: 'block' }} />
        </a>
      </div>

      <div className="bs-legal" style={{ paddingTop: 64, paddingBottom: 80, maxWidth: 820 }}>
        <div style={{ ...MONO, fontSize: 14, letterSpacing: F.tracking.wide, color: C.link, marginBottom: 12 }}>{doc.eyebrow}</div>
        <h1 style={{ margin: 0, ...DISP, fontWeight: 800, fontSize: 48, letterSpacing: F.tracking.tightest, color: C.ink }}>{doc.title}</h1>
        <div style={{ ...MONO, fontSize: 14, color: C.textMuted, marginTop: 12, marginBottom: 40 }}>Last updated {doc.updated}</div>

        {doc.sections.map((s) => (
          <section key={s.h} style={{ marginBottom: 34 }}>
            <h2 style={{ ...DISP, fontWeight: 700, fontSize: 22, letterSpacing: F.tracking.tight, color: C.ink, margin: '0 0 10px' }}>{s.h}</h2>
            {s.p.map((para, i) => (
              <p key={i} style={{ margin: '0 0 10px', fontSize: 16, lineHeight: 1.6, color: C.text }}>{para}</p>
            ))}
          </section>
        ))}

        <div style={{ borderTop: `1px solid ${C.hairline}`, paddingTop: 20, marginTop: 8 }}>
          <a href="/" style={{ color: C.link, fontWeight: 600, textDecoration: 'none' }}>← Back to byrdsense.com</a>
        </div>
      </div>

      <footer style={{ background: C.ink, color: '#FFFFFF' }}>
        <div className="bs-legal" style={{ paddingTop: 24, paddingBottom: 28, display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'center', ...MONO, fontSize: 14 }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: C.textOnDarkMuted }}>Byrdsense</span>
            <span style={{ fontFamily: F.body, fontSize: 14, color: C.textOnDarkMuted }}>{noOrphan(TAGLINE)}</span>
          </span>
          <span style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <a href="tel:4237904745" style={{ color: C.accent, textDecoration: 'none' }}>{CONTACT_PHONE}</a>
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: C.accent, textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
            <a href="/privacy" style={{ color: C.textOnDarkMuted, textDecoration: 'none' }}>Privacy</a>
            <a href="/terms" style={{ color: C.textOnDarkMuted, textDecoration: 'none' }}>Terms</a>
          </span>
        </div>
      </footer>
    </div>
  );
}

export function PrivacyPage() { return <LegalPage doc={PRIVACY} />; }
export function TermsPage() { return <LegalPage doc={TERMS} />; }
export default LegalPage;
